import { TenantEntitlementsI } from './tenant';

/**
 * Why a tenant can (or cannot) access the platform, derived from the backend's
 * own rule (platform-entitlement-api.service.ts):
 *
 *   canAccessPlatform = trialActive || subscriptionActive || licenseActive
 *
 * A tenant on an active trial or subscription legitimately has NO License row,
 * so "no license" is NOT an error state for them (#2305). Lives in
 * `data-models-ui` (#2347) so both `LicenseVerifyComponent`
 * (`@bodyanalytics/front/admin/license/feature`) and `EntitlementBadgeComponent`
 * (`@bodyanalytics/front/admin/manage-tenants-ui/ui`) — two different features —
 * can derive access through this one function without a cross-feature
 * `ui → domain` import.
 *
 *  - `license`      → an active license row exists; show license details.
 *  - `subscription` → access comes from an ACTIVE/TRIALING subscription.
 *  - `trial`        → access comes from the tenant trial window.
 *  - `none`         → `canAccessPlatform` is false; the ONLY state that may
 *                     render an error/expired banner.
 */
export type EntitlementAccessSource = 'license' | 'subscription' | 'trial' | 'none';

/** Mirrors the backend's `subscriptionActive` statuses (ACTIVE | TRIALING). */
const ACTIVE_SUBSCRIPTION_STATUSES: ReadonlyArray<TenantEntitlementsI['subscriptionStatus']> = ['ACTIVE', 'TRIALING'];

export function deriveEntitlementAccess(entitlements: TenantEntitlementsI): EntitlementAccessSource {
  if (!entitlements.canAccessPlatform) {
    return 'none';
  }
  if (entitlements.licenseActive) {
    return 'license';
  }
  if (ACTIVE_SUBSCRIPTION_STATUSES.includes(entitlements.subscriptionStatus)) {
    return 'subscription';
  }
  if (entitlements.trialActive) {
    return 'trial';
  }
  // canAccessPlatform is true but no source is recognizable client-side (e.g. a
  // new subscription status the frontend does not know yet). Trust the server
  // flag — the backend's only remaining access path is the subscription.
  return 'subscription';
}
